import React, { useEffect, useState } from 'react';
import { Hourglass, CheckCircle, Prohibit, UsersThree, Key } from '@phosphor-icons/react';
import { api } from '../api';
import { showToast } from '../components/Toast';
import { TextEdit } from '../components/EditableCell';

const ROLE_OPTIONS = ['Sales', 'SalesMgr', 'WorkshopMgr', 'Mechanic', 'Detail', 'DetailMgr', 'Tint', 'TintMgr', 'DeliveryMgr', 'Manager'];

const TEAM_TITLES = {
  SalesMgr:    { title: 'Sales Team',    subtitle: 'Approve new salespeople · manage your team' },
  WorkshopMgr: { title: 'Workshop Team', subtitle: 'Approve new mechanics · manage your team' },
  DetailMgr:   { title: 'Detail Team',   subtitle: 'Approve new detailers · manage your team' },
  TintMgr:     { title: 'Tint Team',     subtitle: 'Approve new tinters · manage your team' },
  Manager:     { title: 'Users',         subtitle: 'Manage team access · approve signups · change roles' },
};

function StatusBadge({ status }) {
  const map = {
    pending:  { cls: 'prog', label: 'Pending' },
    active:   { cls: 'done', label: 'Active' },
    rejected: { cls: 'ns',   label: 'Rejected' },
  };
  const { cls, label } = map[status] || map.pending;
  return <span className={`sb ${cls}`}>{label}</span>;
}

const MGR_CREATES = { SalesMgr: 'Sales', WorkshopMgr: 'Mechanic', DetailMgr: 'Detail', TintMgr: 'Tint' };

const blankNew = { name: '', role: 'Sales', email: '', phone: '', password: '' };

export default function Users({ session, refreshKey, onRefresh }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [draft, setDraft]     = useState(blankNew);
  const [saving, setSaving]   = useState(false);
  const isFullManager = session.user.role === 'Manager';
  // A department manager can only create the role they already approve
  const creatableRoles = isFullManager ? ROLE_OPTIONS : [MGR_CREATES[session.user.role]].filter(Boolean);

  function reload() {
    setLoading(true);
    api.listUsers()
      .then(setUsers)
      .catch((e) => showToast('Failed: ' + e.message))
      .finally(() => setLoading(false));
  }

  useEffect(reload, [refreshKey]);

  async function approve(u) {
    try { await api.approveUser(u.id); showToast(`${u.name} approved`); reload(); onRefresh?.(); }
    catch (e) { showToast('Failed: ' + e.message); }
  }
  async function reject(u) {
    if (!window.confirm(`Reject access for ${u.name}?`)) return;
    try { await api.rejectUser(u.id); showToast(`${u.name} rejected`); reload(); onRefresh?.(); }
    catch (e) { showToast('Failed: ' + e.message); }
  }
  async function changeRole(u, role) {
    if (role === u.role) return;
    if (!window.confirm(`Change ${u.name}'s role to ${role}?`)) return;
    try { await api.changeUserRole(u.id, role); showToast(`${u.name} → ${role}`); reload(); }
    catch (e) { showToast('Failed: ' + e.message); }
  }
  async function resetPassword(u) {
    const pw = window.prompt(`Set a new password for ${u.name}:`);
    if (!pw) return;
    if (pw.length < 4) return showToast('Password must be at least 4 characters');
    try { await api.resetUserPassword(u.id, pw); showToast(`Password reset for ${u.name}`); }
    catch (e) { showToast('Failed: ' + e.message); }
  }
  async function remove(u) {
    if (!window.confirm(`Permanently delete ${u.name}?`)) return;
    try { await api.deleteUser(u.id); showToast(`${u.name} deleted`); reload(); }
    catch (e) { showToast('Failed: ' + e.message); }
  }
  async function saveContact(u, patch) {
    const before = users;
    setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, ...patch } : x)));
    try { await api.updateUserContact(u.id, { email: u.email, phone: u.phone, ...patch }); }
    catch (e) { setUsers(before); showToast('Failed: ' + e.message); }
  }

  async function createUser(e) {
    e.preventDefault();
    if (saving) return;
    if (draft.name.trim().length < 2)   return showToast('Name must be at least 2 characters');
    if (draft.password.length < 4)      return showToast('Password must be at least 4 characters');
    setSaving(true);
    try {
      const u = await api.createUser({ ...draft, name: draft.name.trim() });
      showToast(`${u.name} added — they can sign in now`);
      setDraft(blankNew);
      setAdding(false);
      reload();
      onRefresh?.();
    } catch (err) {
      showToast('Failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading">Loading users…</div>;

  const pending = users.filter((u) => u.status === 'pending');
  const active  = users.filter((u) => u.status === 'active');
  const rejected= users.filter((u) => u.status === 'rejected');

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">{(TEAM_TITLES[session.user.role] || TEAM_TITLES.Manager).title}</div>
          <div className="ps">{(TEAM_TITLES[session.user.role] || TEAM_TITLES.Manager).subtitle}</div>
        </div>
        {creatableRoles.length > 0 && (
          <div className="ph-actions">
            <button className="ab p" onClick={() => setAdding((v) => !v)}>
              {adding ? 'Cancel' : '+ Add User'}
            </button>
          </div>
        )}
      </div>

      {adding && (
        <form className="af newuser" onSubmit={createUser}>
          <div className="sec" style={{ marginBottom: 12 }}>New account</div>
          <div className="fg2">
            <div>
              <label className="afl">Name <span className="req">*</span></label>
              <input className="afi" placeholder="First name" autoFocus
                value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label className="afl">Role <span className="req">*</span></label>
              <select className="afi" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
                {creatableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="fg2">
            <div>
              <label className="afl">Email</label>
              <input className="afi" type="email" placeholder="name@melvillemotors.com.au"
                value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </div>
            <div>
              <label className="afl">Phone</label>
              <input className="afi" type="tel" placeholder="0400 000 000"
                value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </div>
          </div>
          <label className="afl">Starting password <span className="req">*</span></label>
          <input className="afi" type="text" placeholder="At least 4 characters"
            value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
          <div style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 14px' }}>
            Tell them this password in person — they can keep it or you can reset it later.
            The account is active straight away, no approval needed.
          </div>
          <button type="submit" className="btn-in" disabled={saving}>
            {saving ? 'Creating…' : 'Create account'}
          </button>
        </form>
      )}

      <div className="stats">
        <StatCard icon={Hourglass}  cls="am" value={pending.length}  label="Pending" />
        <StatCard icon={CheckCircle} cls="gr" value={active.length}   label="Active" />
        <StatCard icon={Prohibit}   cls="r"  value={rejected.length} label="Rejected" />
        <StatCard icon={UsersThree} cls="bl" value={users.length}    label="Total" />
      </div>

      {pending.length > 0 && (
        <Section title="Pending Approval" subtitle="Click Approve to grant access — or Reject to deny.">
          <UsersTable users={pending} session={session} onSaveContact={saveContact}>
            {(u) => (
              <>
                <button className="ab g" onClick={() => approve(u)}>✓ Approve</button>
                <button className="ab r" onClick={() => reject(u)}  style={{ marginLeft: 6 }}>Reject</button>
              </>
            )}
          </UsersTable>
        </Section>
      )}

      <Section title="Active Users" subtitle={`${active.length} member${active.length === 1 ? '' : 's'} can sign in`}>
        <UsersTable
          users={active}
          session={session}
          showRoleSelect={isFullManager}
          onChangeRole={changeRole}
          onSaveContact={saveContact}
        >
          {(u) => (
            <>
              {isFullManager && (
                <button className="ab" onClick={() => resetPassword(u)}><Key size={12} weight="duotone" style={{ verticalAlign: '-2px', marginRight: 3 }} />Reset Pwd</button>
              )}
              {isFullManager && u.id !== session.user.id && (
                <button className="ab r" onClick={() => remove(u)} style={{ marginLeft: 6 }}>Delete</button>
              )}
              {!isFullManager && (
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>Active</span>
              )}
            </>
          )}
        </UsersTable>
      </Section>

      {rejected.length > 0 && (
        <Section title="Rejected" subtitle="Past denials">
          <UsersTable users={rejected} session={session}>
            {(u) => (
              <>
                <button className="ab g" onClick={() => approve(u)}>Re-approve</button>
                {isFullManager && (
                  <button className="ab r" onClick={() => remove(u)} style={{ marginLeft: 6 }}>Delete</button>
                )}
              </>
            )}
          </UsersTable>
        </Section>
      )}

      {isFullManager && <NotificationsPanel />}
    </div>
  );
}

/**
 * Whether the salesperson "car is ready" notices can actually go out, and a
 * button to prove it — so this can be checked without waiting for a real car
 * to finish.
 */
function NotificationsPanel() {
  const [st, setSt]       = useState(null);
  const [busy, setBusy]   = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.notifyStatus().then(setSt).catch((e) => setSt({ error: e.message }));
  }, []);

  async function sendTest() {
    setBusy(true); setResult(null);
    try { setResult(await api.notifyTest()); }
    catch (e) { setResult({ error: e.message }); }
    finally { setBusy(false); }
  }

  const dot = (ok) => (
    <span style={{
      width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 7,
      background: ok ? 'var(--green)' : 'var(--muted)',
    }} />
  );

  return (
    <Section title="Notifications" subtitle="Salespeople are told when every stage on their car is complete.">
      <div className="af" style={{ maxWidth: 560 }}>
        {!st && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Checking…</div>}
        {st?.error && <div style={{ fontSize: 13, color: 'var(--red)' }}>{st.error}</div>}
        {st && !st.error && (
          <>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              {dot(st.email.ok)}
              <strong>Email</strong> — {st.email.ok
                ? `connected to ${st.email.host}`
                : (st.email.enabled ? `not working: ${st.email.error}` : 'not set up yet')}
            </div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              {dot(st.push.enabled)}
              <strong>Phone notifications</strong> — {st.push.enabled
                ? `on · ${st.push.devices_registered} device${st.push.devices_registered === 1 ? '' : 's'} registered`
                : 'not set up yet'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 14 }}>
              {dot(st.accounts_with_email > 0)}
              <strong>{st.accounts_with_email}</strong> account{st.accounts_with_email === 1 ? '' : 's'} have an email address
            </div>

            <button className="ab p" onClick={sendTest} disabled={busy}>
              {busy ? 'Sending…' : 'Send me a test'}
            </button>

            {result && (
              <div style={{
                marginTop: 12, fontSize: 12, lineHeight: 1.6,
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 12px',
              }}>
                {result.error ? (
                  <span style={{ color: 'var(--red)' }}>{result.error}</span>
                ) : (
                  <>
                    <div><strong>Sent to:</strong> {result.to}</div>
                    <div><strong>Email:</strong> {result.result.email}</div>
                    <div><strong>Phone:</strong> {result.result.push}</div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Section>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div className="wgt" style={{ marginTop: 6 }}>
        <div className="wgt-dot"></div>{title}
      </div>
      {subtitle && <div className="ps" style={{ marginTop: -12, marginBottom: 12 }}>{subtitle}</div>}
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, cls, value, label }) {
  return (
    <div className="sc">
      <div className={`sc-icon ${cls}`}><Icon size={20} weight="duotone" /></div>
      <div className="sc-data">
        <div className="sn">{value}</div>
        <div className="sl">{label}</div>
      </div>
    </div>
  );
}

function UsersTable({ users, session, showRoleSelect, onChangeRole, onSaveContact, children }) {
  if (!users.length) {
    return (
      <div className="tw">
        <table className="vt">
          <tbody>
            <tr className="empty"><td>No users in this list.</td></tr>
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="tw">
      <table className="vt">
        <thead>
          <tr>
            <th>Name</th>
            <th>Role</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Status</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="row-even">
              <td>
                <div className="vm">
                  {u.name}
                  {u.id === session.user.id && (
                    <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--accent)', fontWeight: 700 }}>YOU</span>
                  )}
                </div>
              </td>
              <td>
                {showRoleSelect ? (
                  <select
                    className="wfsel"
                    value={u.role}
                    onChange={(e) => onChangeRole(u, e.target.value)}
                    style={{ width: 130, padding: '4px 8px' }}
                    disabled={u.id === session.user.id}
                  >
                    {ROLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                ) : (
                  <span className="vs" style={{ fontSize: 13, fontWeight: 600 }}>{u.role}</span>
                )}
              </td>
              <td>
                <TextEdit
                  value={u.email}
                  placeholder="Add email"
                  onChange={(val) => onSaveContact?.(u, { email: val })}
                  disabled={!onSaveContact}
                />
              </td>
              <td>
                <TextEdit
                  value={u.phone}
                  placeholder="Add phone"
                  onChange={(val) => onSaveContact?.(u, { phone: val })}
                  disabled={!onSaveContact}
                />
              </td>
              <td><StatusBadge status={u.status} /></td>
              <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                {u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '—'}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{children(u)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
